import { getMembershipPlanType, isFrictionlessSignupUser } from '@frontend/api-client';
import { useMemo, useState, useVisibilityController } from '@frontend/react';
import { buildDisplayableMembership } from '@frontend/react-domain';
import { Button, Divider, Text, XStack, YStack } from '@frontend/web-react';
import { CalloutBadge } from '@frontend/web-react/components/Primitives/Badges';
import { PlanManagementModal } from '@frontend/web/components/DomainSpecific/Account/PlanManagement';
import { PaywallModal } from '@frontend/web/components/DomainSpecific/Paywall';
import { usePricingTerminology } from '@frontend/web/context';
import { useMembershipPlans, useRetrieveClientQuery } from '@frontend/web/hooks';

export const CurrentMembershipCard = () => {
  const [isPlanManagementModalOpen, setIsPlanManagementModalOpen] = useState(false);
  const [isPaywallModalOpen, { close: closePaywallModal, open: openPaywallModal }] = useVisibilityController();
  const { data: client, isLoading: isLoadingClient, refetch } = useRetrieveClientQuery();
  const isTrial = isFrictionlessSignupUser(client);
  const { showCredits } = usePricingTerminology();
  const { isError: isErrorPlans, isLoading: isLoadingPlans, planLookup } = useMembershipPlans();
  const currentMembershipPlanType = useMemo(() => (client ? getMembershipPlanType(client) : undefined), [client]);
  const currentPlanDetails = useMemo(() => buildDisplayableMembership(client, planLookup), [client, planLookup]);
  const currentPlan = currentMembershipPlanType ? planLookup[currentMembershipPlanType] : undefined;

  if (isLoadingClient || isLoadingPlans) {
    return <LoadingMembershipCard />;
  }

  if (!currentPlanDetails || isErrorPlans) {
    return (
      <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="lg" p="md" radius="lg">
        <YStack gap="xs">
          <Text color="primary" type="h5">
            Something went wrong.
          </Text>
          <Text color="primary" type="p-sm">
            We are unable to locate your current membership plan. Please contact support.
          </Text>
        </YStack>
      </YStack>
    );
  }

  return (
    <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="lg" p="md" radius="lg">
      <YStack gap="xs">
        <XStack gap="xs">
          <Text color="primary" type="h5">
            {currentPlanDetails.membershipPlanName}
          </Text>

          {!currentPlanDetails.isOnFreeTier ? (
            <>
              <Text color="secondary" type="p-md">
                {currentPlanDetails.price}/{currentPlanDetails.interval}
              </Text>
              {currentPlanDetails.pendingChange ? <CalloutBadge>CHANGE PENDING</CalloutBadge> : null}
            </>
          ) : null}
        </XStack>

        {currentPlanDetails.isOnFreeTier ? (
          <Text color="secondary" type="p-sm">
            Upgrade to a membership plan to unlock savings.
          </Text>
        ) : (
          <>
            <Text color="primary" type="p-sm">
              {showCredits
                ? `${currentPlanDetails.includedCredits} credits per month`
                : currentPlan
                  ? `~${currentPlan.estimatedTasksPerMonth.min}-${currentPlan.estimatedTasksPerMonth.max} tasks/mo`
                  : ''}
            </Text>
            {currentPlanDetails.pendingChange ? (
              <YStack gap="sm" py="md">
                <Divider />
                <Text color="primary" type="p-sm">
                  Upcoming plan change: {currentPlanDetails.nextMembershipPlan?.membershipPlanName}
                </Text>
                <Text color="primary" type="p-sm">
                  Effective on: {currentPlanDetails.nextBillingDate}
                </Text>
              </YStack>
            ) : (
              <Text color="primary" type="p-sm">
                Next Renewal Date: {currentPlanDetails.nextBillingDate}
              </Text>
            )}
          </>
        )}
      </YStack>
      <Button
        label={currentPlanDetails.isOnFreeTier ? 'Upgrade Membership' : 'Manage Subscription'}
        onClick={isTrial ? openPaywallModal : () => setIsPlanManagementModalOpen(true)}
        size="sm"
      />
      {isTrial ? (
        <PaywallModal
          isFrictionlessUser
          isOpen={isPaywallModalOpen}
          onClose={closePaywallModal}
          onSuccess={async () => {
            await refetch();
            closePaywallModal();
          }}
        />
      ) : (
        <PlanManagementModal
          closeModal={() => setIsPlanManagementModalOpen(false)}
          initialState={{
            step: 'selectSubscription',
            ...(currentMembershipPlanType && {
              currentSubscriptionPlan: { planType: currentMembershipPlanType },
            }),
          }}
          isOpen={isPlanManagementModalOpen}
          onPlanChange={() => refetch()}
        />
      )}
    </YStack>
  );
};

const LoadingMembershipCard = () => (
  <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="sm" p="md" radius="lg">
    <YStack bg="lineGray" height={20} radius="lg" width="50%"></YStack>
    <YStack bg="lineGray" height={20} radius="lg" width="100%"></YStack>
  </YStack>
);
